import { KB_TITLE_MAX_CHARS, type FailureCode, type KbSource, type KbSourceStatus } from '@rp/contracts'
import { failureText } from '../../api/copy'
import type { StatusTone } from '../../design'

const TOKEN_FORMAT = new Intl.NumberFormat('en-US')

export const KB_COPY = {
  title: '知識庫',
  sources: '知識庫來源',
  addTitle: '新增來源',
  kind: '來源類型',
  kinds: { upload: '上傳檔案', url: '網址' },
  sourceTitle: '標題',
  uri: '網址',
  file: '檔案',
  fileHint: (maxBytes: number) => `支援 PDF、純文字、Markdown 或 HTML，最大 ${Math.round(maxBytes / 1024 / 1024)}MB`,
  unsupportedFile: '不支援這種檔案格式',
  fileTooLarge: '檔案超過大小上限',
  emptyFile: '這個檔案沒有內容，匯入之後也答不到任何問題',
  titleTooLong: `標題不可超過 ${KB_TITLE_MAX_CHARS} 個字`,
  submit: '新增來源',
  submitting: '新增中…',
  download: '下載',
  downloading: '下載中…',
  downloadFailed: '下載失敗，請再試一次',
  remove: '移除',
  removing: '移除中…',
  retryIngest: '重新匯入',
  retryingIngest: '重新匯入中…',
  budgetStopped: '本月 AI 用量已達上限，匯入已停止，這次的內容沒有加入知識庫。預算調高或下月額度重置後，按「重新匯入」即可繼續。',
  budgetTooLarge: (neededTokens: number, remainingTokens: number) =>
    `這份文件最多需要 ${TOKEN_FORMAT.format(neededTokens)} tokens，本月只剩 ${TOKEN_FORMAT.format(remainingTokens)} tokens，所以沒有開始匯入，也沒有用掉任何額度。預算調高或下月額度重置後，按「重新匯入」即可繼續。`,
  confirmRemoveTitle: '確認移除來源',
  confirmRemoveBody: (title: string) => `確定要移除「${title}」嗎？依據它產生的核准回覆會自動下架。`,
  confirmRemoveCancel: '取消',
  confirmRemoveConfirm: '確認移除',
  chunkCount: (count: number) => `${count} 個片段`,
  version: (version: number) => `第 ${version} 版`,
  pagination: '分頁',
  previousPage: '上一頁',
  nextPage: '下一頁',
  pageStatus: (page: number, total: number, limit: number) =>
    `第 ${page} 頁，共 ${Math.max(1, Math.ceil(total / limit))} 頁（${total} 個項目）`,
  retireWarning: '移除或更新來源時，依據它產生的核准回覆會自動下架，交給核准人員重新審閱。',
  forbidden: '你沒有權限管理知識庫',
  forbiddenBody: '請聯絡公司的管理員。',
  loading: '載入知識庫…',
  loadFailed: '載入知識庫失敗',
  retry: '重新載入',
  empty: '還沒有任何知識庫來源',
  emptyBody: '上傳價目表、常見問題或政策文件，AI 才有依據草擬建議。',
  sandboxTitle: '上線前測試',
  sandboxHint: '輸入客人可能問的問題，看看現時的知識庫能不能撐起答案。',
  question: '測試問題',
  test: '測試',
  testing: '測試中…',
  answerable: '可以回答',
  notAnswerable: '沒有把握的建議',
  topSimilarity: (value: number) => `最高相似度 ${Math.round(value * 100)}%`,
  threshold: (value: number) => `門檻 ${Math.round(value * 100)}%`,
} as const

export const KB_SOURCE_STATUS_LABELS: Record<KbSourceStatus, string> = {
  pending: '等待匯入',
  ingesting: '匯入中',
  ready: '已完成',
  failed: '匯入失敗',
}

const KB_BUDGET_FAILURE: FailureCode = 'budget_exhausted'

export const kbFailureText = (source: Pick<KbSource, 'error' | 'budgetShortfall'>): string => {
  if (source.error === null) return ''
  if (source.error !== KB_BUDGET_FAILURE) return failureText(source.error)
  const shortfall = source.budgetShortfall
  return shortfall === null ? KB_COPY.budgetStopped : KB_COPY.budgetTooLarge(shortfall.neededTokens, shortfall.remainingTokens)
}

export const kbStatusTone = (status: KbSourceStatus): StatusTone =>
  status === 'ready' ? 'success' : status === 'failed' ? 'danger' : 'info'
